import { DEFAULT_SCORING_RULES } from "@/features/predictions/constants/scoringRules";
import type { ScoringExample, ScoringRule, ScoringRules } from "@/features/predictions/types/competition.type";

export type ScoringRulesSection = {
  key: string
  title: string
  rules: ScoringRule[]
};

export type DerivedScoringRules = {
  sections: ScoringRulesSection[]
  examples: ScoringExample[]
  footnotes: string[]
};

// Section titles are presentation, derived from the API's category keys.
const SECTION_TITLE_BY_CATEGORY: Record<string, string> = {
  base: "Points de base",
  bonus: "Bonus phase finale",
  tiebreaker: "En cas d'égalité",
};

const KNOWN_CATEGORY_ORDER = ["base", "bonus", "tiebreaker"];
const UNKNOWN_SECTION_TITLE = "Autres règles";

// Single source of truth for the rules content: the API-served scoring_rules
// when present, DEFAULT_SCORING_RULES otherwise (deploy window, stale caches).
// Same pattern as sponsorship's deriveBareme.
export const deriveScoringRules = (scoringRules: ScoringRules | null): DerivedScoringRules => {
  const source = scoringRules ?? DEFAULT_SCORING_RULES;

  const rulesByCategory = new Map<string, ScoringRule[]>();
  source.rules.forEach((rule) => {
    const existing = rulesByCategory.get(rule.category);
    if (existing) existing.push(rule);
    else rulesByCategory.set(rule.category, [rule]);
  });

  const knownSections = KNOWN_CATEGORY_ORDER
    .filter((category) => rulesByCategory.has(category))
    .map((category) => ({
      key: category,
      title: SECTION_TITLE_BY_CATEGORY[category] ?? UNKNOWN_SECTION_TITLE,
      rules: rulesByCategory.get(category) ?? [],
    }));

  // Unknown categories are never dropped: they land in a trailing neutral
  // section so a new backend vocabulary still renders.
  const unknownRules = Array.from(rulesByCategory.entries())
    .filter(([category]) => !KNOWN_CATEGORY_ORDER.includes(category))
    .flatMap(([, rules]) => rules);
  const sections = unknownRules.length > 0
    ? [...knownSections, { key: "other", title: UNKNOWN_SECTION_TITLE, rules: unknownRules }]
    : knownSections;

  return {
    sections,
    examples: source.examples,
    footnotes: source.footnotes,
  };
};
